"use client";
import { Image } from "@/components/Image";
import { TagHemerys } from "@/components/tags/TagHemerys";
import { TagSmallGroup } from "@/components/tags/TagSmallGroup";
import { SlideTransition } from "@/components/useSlideTransition";
import type { SearchTravel } from "front-search-schema/algolia";
import { useRouter } from "next/navigation";
import { type MouseEventHandler, useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "ui/iconsN";
import styles from "./Images.module.css";

type Props = {
  travel: SearchTravel;
  isFirst: boolean;
  href?: string;
  showSmallGroupTag?: boolean;
};
export const Images = ({ travel, isFirst, href, showSmallGroupTag = false }: Props) => {
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<"right" | "left">("right");
  const isPremium = travel.brandCustomerTitle === "Hémérys";

  const currentImage = travel.travelImages[currentIndex];

  const handlePrevious: MouseEventHandler = (e) => {
    e.stopPropagation();
    setDirection("left");
    setCurrentIndex((i) => (i === 0 ? i : i - 1));
  };

  const handleNext: MouseEventHandler = (e) => {
    e.stopPropagation();
    setDirection("right");
    setCurrentIndex((i) => (i === travel.travelImages.length - 1 ? i : i + 1));
  };

  const handleClick = () => {
    router.push(href ?? `/voyage/${travel.alias}-${travel.id}/`);
  };

  if (!currentImage) return null;

  return (
    <div className="w-full @min-[715px]:w-[330px] shrink-0">
      <div className="relative h-full @max-[715px]:pb-[56.25%] overflow-hidden select-none">
        <SlideTransition
          currentKey={currentImage.id.toString()}
          currentData={{ image: currentImage, isFirst, index: currentIndex }}
          direction={direction}
          timeout={250}
          styles={{
            enterRight: styles.fadeEnterRight,
            enterActiveRight: styles.fadeEnterActiveRight,
            exitRight: styles.fadeExitRight,
            exitActiveRight: styles.fadeExitActiveRight,
            enterLeft: styles.fadeEnterLeft,
            enterActiveLeft: styles.fadeEnterActiveLeft,
            exitLeft: styles.fadeExitLeft,
            exitActiveLeft: styles.fadeExitActiveLeft,
          }}
        >
          {({ image, isFirst: isFirstImg, index }, className) => (
            <div className={`absolute top-0 w-full h-full cursor-pointer ${className}`} onClick={handleClick}>
              <Image
                alt={image.caption || ""}
                sizes="960px"
                image={{
                  type: "travel",
                  id: travel.id.toString(),
                  name: image.name,
                  realname: image.realname || "",
                }}
                objectFit="cover"
                isInitVisible={isFirstImg && index === 0}
                className="h-full"
              />
            </div>
          )}
        </SlideTransition>
        {currentIndex !== 0 && (
          <div
            className="group absolute left-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white w-8 h-16 cursor-pointer rounded-r-full flex justify-center items-center backdrop-blur-xs border border-l-0 border-white/0 shadow-[0_0_16px_0_rgba(0,0,0,0.24)] hover:border-pr-1 hover:shadow-[0_0_24px_0_rgba(185,87,51,0.70)] transition-all select-none"
            onClick={handlePrevious}
          >
            <ArrowLeftIcon className="group-hover:-translate-x-1 transition-transform stroke-tx-1" />
          </div>
        )}
        {currentIndex !== travel.travelImages.length - 1 && (
          <div
            className="group absolute right-0 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white w-8 h-16 cursor-pointer rounded-l-full flex justify-center items-center backdrop-blur-xs border border-r-0 border-white/0 shadow-[0_0_16px_0_rgba(0,0,0,0.24)] hover:border-pr-1 hover:shadow-[0_0_24px_0_rgba(185,87,51,0.70)] transition-all select-none"
            onClick={handleNext}
          >
            <ArrowRightIcon className="group-hover:translate-x-1 transition-transform stroke-tx-1" />
          </div>
        )}
        {(isPremium || showSmallGroupTag) && (
          <div className="pointer-events-none absolute top-3 left-3 flex flex-col items-start gap-2">
            {showSmallGroupTag && <TagSmallGroup />}
            {isPremium && <TagHemerys />}
          </div>
        )}
      </div>
    </div>
  );
};
